import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  GridLegacy as Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { debounce } from 'lodash';
import moment from 'moment';
import React, { useMemo } from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { EmployeeSelect } from '~/components/GenericForm/EmployeeSelect';
import { FieldSetWrapper } from '~/components/GenericForm/FieldSetWrapper';
import { useApplicationConfig } from '~/data/applicationConfig';
import { TabModules, useModulesConfig } from '~/data/company';
import { Enum, useAllEnums } from '~/data/enums';
import { RewardingTabs } from '~/data/tabs';
import {
  PersonnelGroupValues,
  PersonnelGroups,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmploymentDetail/useScheduleEmploymentDetail';
import {
  ChangedStartDateState,
  Form,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import { AppModules } from '~/services/Permissions';
import { convertDateTypeDatePickerProps } from '~/utils/helpers/date';

import {
  EmploymentDetailsCustomData,
  EmploymentStatusComponent,
  JobGradeComponent,
} from './components';
import { useSxStyles } from './useStyles';

type Props = {
  scheduleFormMethods: UseFormReturn<Form>;
  employmentStatuses: Enum[];
  jobGrades: Enum[];
  getPersonnelGroupOptions: (
    groupNumber: number,
    personnelGroupValues: PersonnelGroupValues,
  ) => Enum[];
  index: number;
  fComMaxPersonnelGroups: number;
  newEmpl: boolean;
  setChangedStartDate: React.Dispatch<
    React.SetStateAction<ChangedStartDateState>
  >;
  allResponsibleHRIds: number[];
  pendingProposal: boolean;
  inProgressPropStartDate?: number;
  hrSelectorDisable: boolean;
  newEngine: boolean;
};

export const FormComponent = (props: Props) => {
  const {
    scheduleFormMethods,
    getPersonnelGroupOptions,
    index,
    fComMaxPersonnelGroups,
    newEmpl,
    setChangedStartDate,
    allResponsibleHRIds,
    pendingProposal,
    inProgressPropStartDate,
    hrSelectorDisable,
    newEngine,
  } = props;

  const { control, getValues } = scheduleFormMethods;

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const { allEnums } = useAllEnums();

  const { applicationConfig } = useApplicationConfig();
  const { is7DaysEnabled, vacationCalculationDayOptions } = useMemo(() => {
    const is7DaysEnabled =
      applicationConfig?.fComConfig?.absConfig?.fComEnable7DaysVacationRule ??
      false;

    const selectedDaysRule = getValues(
      `schedule.${index}.employmentDetails.fEmplDetailVacBalCalcSixDays`,
    );

    const vacationCalculationDayOptions =
      is7DaysEnabled || selectedDaysRule === 7 ? [5, 6, 7] : [5, 6];

    return {
      is7DaysEnabled,
      vacationCalculationDayOptions,
    };
  }, [index, getValues, applicationConfig]);

  const { isModuleTabEnabled, isModuleEnabled } = useModulesConfig();
  const { isWorkTimeEnabled } = useMemo(() => {
    return {
      isWorkTimeEnabled:
        isModuleEnabled(AppModules.WORK_TIME_REPORTING) &&
        isModuleTabEnabled(TabModules.REWARDING, RewardingTabs.WorkTime),
    };
  }, [isModuleTabEnabled, isModuleEnabled]);

  const getPersonnelGroupName = (number: number) => {
    const index = number - 1;
    return allEnums.PERSONNEL_GROUP_NAME[index].name;
  };

  const personnelGroupIndexNumbers: number[] = Array.from(
    Array(fComMaxPersonnelGroups).keys(),
  );

  const startDateMinDate = useMemo((): Date | undefined => {
    const employmentStartDate = getValues(
      `schedule.${index}.employment.fEmplDateStart`,
    );

    const inProgressStartDate = !!inProgressPropStartDate
      ? moment(inProgressPropStartDate).toDate()
      : undefined;

    if (inProgressStartDate && employmentStartDate) {
      return moment(inProgressStartDate).isBefore(employmentStartDate)
        ? employmentStartDate
        : inProgressStartDate;
    } else if (!inProgressStartDate && employmentStartDate) {
      return employmentStartDate;
    } else if (!employmentStartDate && inProgressStartDate)
      return inProgressStartDate;
    else return undefined;
  }, [getValues, inProgressPropStartDate, index]);

  const DEBOUNCE_TIMER_MS = 400;

  const onEmploymentDetailStartDateChangeSync = useMemo(
    () =>
      debounce(() => {
        setChangedStartDate((activeValue) => ({
          ...activeValue,
          emplDetailsStartDate: true,
        }));
      }, DEBOUNCE_TIMER_MS),
    [setChangedStartDate],
  );

  const RenderPersonnelGroupField = ({
    groupNumber,
  }: {
    groupNumber: number;
  }) => {
    const emplDetailData = useWatch({
      control,
      name: `schedule.${index}.employmentDetails`,
    });
    const personnelGroupValues: PersonnelGroupValues = emplDetailData
      ? personnelGroupIndexNumbers.reduce(
          (acc, cur) => ({
            ...acc,
            [`PERSONNEL_GROUP_${cur}`]:
              emplDetailData[
                `fEmplDetailPersonnelGroup${cur}` as keyof PersonnelGroups
              ],
          }),
          {},
        )
      : {};
    const fieldName =
      `fEmplDetailPersonnelGroup${groupNumber}` as keyof PersonnelGroups;
    const fieldOptions = getPersonnelGroupOptions(
      groupNumber,
      personnelGroupValues,
    );
    return (
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <Controller
          control={control}
          name={`schedule.${index}.employmentDetails.${fieldName}`}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Autocomplete
              options={fieldOptions}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              onChange={(_, value) => onChange(value?.code || '')}
              value={fieldOptions.find((o) => o.code === value) || null}
              disabled={pendingProposal}
              size={'small'}
              data-testid={`personnel-group-${groupNumber}-autocomplete`}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={getPersonnelGroupName(groupNumber)}
                  error={!!error}
                  helperText={error?.message}
                  data-testid={`personnel-group-${groupNumber}-input`}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}
              fullWidth={true}
            />
          )}
        />
      </Grid>
    );
  };

  const RenderPersonnelGroupFields = () => {
    return (
      <Grid item xs={12}>
        <FieldSetWrapper label={t('laPersonnelGroups')}>
          <Grid container columnSpacing={2} rowSpacing={2}>
            {personnelGroupIndexNumbers.map((number) => (
              <RenderPersonnelGroupField
                groupNumber={number + 1}
                key={number}
              />
            ))}
          </Grid>
        </FieldSetWrapper>
      </Grid>
    );
  };

  return (
    <Grid container columnSpacing={2} rowSpacing={2}>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <EmploymentStatusComponent
          control={control}
          index={index}
          getValues={getValues}
          disabled={pendingProposal}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <JobGradeComponent
          control={control}
          index={index}
          getValues={getValues}
          disabled={pendingProposal}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <Controller
          control={control}
          name={`schedule.${index}.employmentDetails.fEmplDetailTaxNumber`}
          render={({ field: { onChange, value } }) => (
            <TextField
              value={value}
              onChange={onChange}
              label={t('fEmplDetailTaxNumber')}
              disabled={pendingProposal}
              size={'small'}
              fullWidth={true}
              data-testid="tax-number-input"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          )}
        />
      </Grid>
      <RenderPersonnelGroupFields />
      <Grid item xs={12}>
        <EmploymentDetailsCustomData
          control={control}
          index={index}
          pendingProposal={pendingProposal}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <Controller
          name={`schedule.${index}.employmentDetails.fEmplDetailVacBalCalcSixDays`}
          control={control}
          render={({ field: { onChange, value } }) => {
            return (
              <FormControl>
                <FormLabel id={'vacation-balance-calculation-label'}>
                  {t('laVacBalCal')}
                </FormLabel>
                <RadioGroup
                  row
                  aria-labelledby={'vacation-balance-calculation-label'}
                  name={'fEmplDetailVacBalCalcSixDays'}
                  value={value}
                  onChange={(event) => {
                    const value = parseInt(event.target.value);
                    if (!isNaN(value)) {
                      onChange(value);
                    }
                  }}
                  data-testid="vacation-balance-calculation-radio-group"
                >
                  {vacationCalculationDayOptions.map((option) => {
                    return (
                      <FormControlLabel
                        key={`balance-calculation-option-${option}`}
                        value={option}
                        control={
                          <Radio
                            data-testid={`vacation-balance-${option}-radio-button`}
                          />
                        }
                        label={option}
                        disabled={
                          pendingProposal || (option === 7 && !is7DaysEnabled)
                        }
                      />
                    );
                  })}
                </RadioGroup>
              </FormControl>
            );
          }}
        />
      </Grid>
      {isWorkTimeEnabled && (
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Controller
            name={`schedule.${index}.employmentDetails.fEmplDetailCalcHoursBalance`}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox data-testid="calculate-hours-balance-checkbox" />
                  }
                  checked={value === 'true'}
                  onChange={(_, checked) => onChange(checked ? 'true' : '')}
                  disabled={pendingProposal}
                  label={t('laCalcHoursBalance')}
                />
                {!!error && (
                  <Typography variant={'caption'} sx={sxStyles.errorText}>
                    {error?.message}
                  </Typography>
                )}
              </Box>
            )}
          />
        </Grid>
      )}
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <Controller
          name={`schedule.${index}.employmentDetails.fEmplDetailStartDate`}
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            const minDate: Date | undefined = startDateMinDate;
            const maxDate: Date | undefined =
              getValues(
                `schedule.${index}.employment.fEmplLastDateEmployment`,
              ) || undefined;

            return (
              <DatePicker
                label={t('laStartDate')}
                disabled={newEmpl || pendingProposal}
                {...convertDateTypeDatePickerProps({
                  value,
                  onChange: (value: Date | null, context) => {
                    onChange(value, context);
                    onEmploymentDetailStartDateChangeSync();
                  },
                  minDate,
                  maxDate,
                })}
                slotProps={{
                  textField: {
                    error: !!error,
                    helperText: error?.message,
                    size: 'small',
                    required: true,
                    fullWidth: true,
                    InputLabelProps: { shrink: true },
                  },
                }}
              />
            );
          }}
        />
      </Grid>
      {index === 0 && newEngine && (
        <Grid item xs={12} md={6}>
          <Controller
            name={`schedule.${index}.employmentDetails.fEmplDetailProposalHrId`}
            control={control}
            rules={{ required: true }}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <EmployeeSelect
                  FormControlProps={{ fullWidth: true, error: !!error }}
                  label={t('laHRResponsible')}
                  value={value || ''}
                  onChange={onChange}
                  optionEmployeeIds={allResponsibleHRIds}
                  fullWidth={true}
                  required={true}
                  disabled={pendingProposal || hrSelectorDisable}
                  size="small"
                />
              );
            }}
          />
        </Grid>
      )}
    </Grid>
  );
};
